import React from 'react';
import { V1SectionContainer } from './V1SectionContainer';
import { MapPin, Navigation } from 'lucide-react';

interface LocationData {
  name?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  full_address?: string;
  directions_url?: string;
  latitude?: number;
  longitude?: number;
  fullAddress?: string;
}

interface Section {
  id: string;
  typeOf: string;
  customTitle?: string;
  sectionOption?: any;
  sectionData?: LocationData | null;
}

interface V1LocationSectionProps {
  section: Section;
}

/**
 * V1 Location Section - FROZEN
 *
 * Displays event location with address and optional map.
 * Shows venue name, address, and directions link.
 *
 * DO NOT add V2 features to this component.
 */
export const V1LocationSection: React.FC<V1LocationSectionProps> = ({ section }) => {
  const location = section.sectionData;

  if (!location || (!location.name && !location.address1 && !location.full_address)) {
    return (
      <V1SectionContainer section={section}>
        {section.customTitle && (
          <h2 className="text-2xl font-bold mb-4">{section.customTitle}</h2>
        )}
        <div className="text-center py-8">
          <MapPin className="mx-auto h-12 w-12 text-gray-400 mb-3" />
          <p className="text-gray-500">Location information not available.</p>
        </div>
      </V1SectionContainer>
    );
  }

  // Generate Google Maps embed URL
  const getMapUrl = () => {
    if (!location) return null;

    const address = location.fullAddress || [
      location.name,
      location.address1,
      location.address2,
      location.city,
      location.state,
      location.zip,
      location.country
    ].filter(Boolean).join(', ');

    if (!address.trim()) return null;

    return `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(address)}`;
  };

  const mapUrl = getMapUrl();

  return (
    <V1SectionContainer section={section}>
      {section.customTitle && (
        <h2 className="text-2xl font-bold mb-6">{section.customTitle}</h2>
      )}

      <div className="space-y-4">
        {/* Location Details */}
        <div className="flex items-start space-x-3">
          <MapPin className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            {location.name && (
              <h3 className="font-semibold text-gray-900">{location.name}</h3>
            )}
            <div className="text-gray-700 mt-1">
              {location.address1 && <div>{location.address1}</div>}
              {location.address2 && <div>{location.address2}</div>}
              {(location.city || location.state || location.zip) && (
                <div>
                  {[location.city, location.state, location.zip]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              )}
              {location.country && <div>{location.country}</div>}
            </div>
          </div>
        </div>

        {/* Directions Link */}
        {location.directions_url && (
          <div className="pt-2">
            <a
              href={location.directions_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
            >
              <Navigation className="h-4 w-4 mr-1" />
              Get Directions
            </a>
          </div>
        )}

        {/* Embedded Map */}
        {mapUrl && (
          <div className="mt-6 rounded-lg overflow-hidden border border-gray-200">
            <iframe
              width="100%"
              height="400"
              frameBorder="0"
              style={{ border: 0 }}
              referrerPolicy="no-referrer-when-downgrade"
              src={mapUrl}
              allowFullScreen
              title="Event Location Map"
            />
          </div>
        )}
      </div>
    </V1SectionContainer>
  );
};